/**
 * Formatting for provider balances: the provider credits page and the
 * superadmin console's Providers tab.
 *
 * A balance arrives already normalized by the backend as
 * `{ type, unit, remaining, limit, used, used_percent, low, resets_at, plan,
 * note, reason, ...provider extras }`. `null` always means "the provider does
 * not report this", never zero, so nothing here turns a missing figure into 0.
 * Only `unit === 'usd'` is formatted as money.
 */

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const formatNumber = (value, maximumFractionDigits = 2) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value);

export function formatBalance(value, unit) {
  if (value == null) return 'Not reported';
  if (unit === 'usd') return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)} ${unit || ''}`.trim();
}

export const BALANCE_TYPE_LABELS = {
  credits: 'Credit balance',
  wallet: 'Prepaid wallet',
  subscription: 'Subscription credits',
  usage_based: 'Usage-based billing',
  spend_limit: 'Monthly spend limit',
  spend: 'Month-to-date spend',
  email_quota: 'Email quota',
  quota: 'Usage quota',
  unknown: 'Balance type unknown',
};

/** 'past_due' -> 'Past due'. Empty input gives an empty string. */
export const humanize = (value) => {
  if (value == null || value === '') return '';
  const text = String(value).replaceAll('_', ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const balanceTypeLabel = (type) => (type ? BALANCE_TYPE_LABELS[type] || humanize(type) : null);

/**
 * The hero figure of a balance card: what is left, or, when the provider only
 * reports consumption, what was spent. Null when neither is known — the
 * caller then explains why instead of printing a fake zero.
 */
export function balanceHeadline(balance) {
  if (!balance || typeof balance !== 'object') return null;
  const remaining = toNumber(balance.remaining);
  if (remaining != null) {
    let label = 'Remaining';
    if (balance.type === 'spend_limit') label = 'Left under spend limit';
    else if (balance.type === 'email_quota') {
      label = balance.binding_window === 'daily' ? 'Emails left today' : 'Emails left this month';
    }
    return { label, value: formatBalance(remaining, balance.unit) };
  }
  const used = toNumber(balance.used);
  if (used != null) {
    return {
      label: balance.type === 'email_quota' ? 'Sent this month' : 'Spent this month',
      value: formatBalance(used, balance.unit),
    };
  }
  return null;
}

/**
 * Percent of the limit consumed (0..100+). Uses the provider's own figure when
 * it sends one, otherwise derives it from used / limit.
 */
export function usedPercent(balance) {
  if (!balance || typeof balance !== 'object') return null;
  const reported = toNumber(balance.used_percent);
  if (reported != null) return reported;
  const used = toNumber(balance.used);
  const limit = toNumber(balance.limit);
  if (used == null || limit == null || limit <= 0) return null;
  return (used / limit) * 100;
}

export const formatPercentValue = (percent) => {
  const value = toNumber(percent);
  if (value == null) return null;
  return `${formatNumber(value, Math.abs(value) < 10 ? 1 : 0)}%`;
};

/**
 * '92% used · Bandwidth': a legacy quota plan reports only a percentage of
 * its most-used resource. Null when there is no percentage.
 */
export function quotaUsage(balance) {
  if (!balance || typeof balance !== 'object') return null;
  const percent = formatPercentValue(balance.used_percent);
  if (percent == null) return null;
  const resource = humanize(balance.limiting_resource);
  return resource ? `${percent} used · ${resource}` : `${percent} used`;
}

/** '3,800 of 10,000 credits' or '$12.30 of $100.00'. Null when either side is unknown. */
export function formatUsedOf(used, limit, unit) {
  const usedValue = toNumber(used);
  const limitValue = toNumber(limit);
  if (usedValue == null || limitValue == null) return null;
  if (unit === 'usd') return `${formatBalance(usedValue, 'usd')} of ${formatBalance(limitValue, 'usd')}`;
  return `${formatNumber(usedValue)} of ${formatBalance(limitValue, unit)}`;
}

/** '≈ 4.2 credits/day'. Zero is a real answer: nothing was consumed. */
export function formatBurnRate(rate, unit) {
  const value = toNumber(rate);
  if (value == null) return null;
  if (value === 0) return 'No decrease';
  return `≈ ${formatBalance(value, unit)}/day`;
}

/** '≈ 27 days left'. Null when there is no forecast (no burn, or no balance). */
export function formatDaysLeft(days) {
  const value = toNumber(days);
  if (value == null || value < 0) return null;
  if (value < 1) return 'Less than a day left';
  if (value >= 365) return 'More than a year left';
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `≈ ${formatNumber(rounded, 1)} ${rounded === 1 ? 'day' : 'days'} left`;
}

const RELATIVE_STEPS = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/**
 * '5 minutes ago'. English on purpose: it is always embedded in an English
 * sentence ("Checked 5 minutes ago"). Everything it describes lies in the
 * past, so a future timestamp only means the browser clock is behind the
 * server's: it reads 'just now' rather than 'in 2 minutes'. Null for a
 * missing or unparseable timestamp.
 */
export function formatRelativeTime(value, now = Date.now()) {
  const time = toTime(value);
  if (time == null) return null;
  const seconds = Math.round((time - now) / 1000);
  if (seconds > -45) return 'just now';
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const step = RELATIVE_STEPS.find(([, size]) => -seconds >= size);
  if (!step) return formatter.format(-1, 'minute');
  return formatter.format(Math.round(seconds / step[1]), step[0]);
}

// A calendar date with no time of day, e.g. Cloudinary's `last_updated`.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A local date and time, or null for a missing or unparseable timestamp.
 *
 * A date-only value stays a date. JavaScript parses '2026-10-12' as UTC
 * midnight, so formatting it in local time would invent a time (05:00 at
 * UTC+5) or, west of UTC, show the previous day.
 */
export function formatDateTime(value) {
  const time = toTime(value);
  if (time == null) return null;
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(time));
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(time));
}

export { toNumber as balanceNumber, toTime as balanceTime };
